import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift } from 'lucide-react';

interface BonusPointsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentAmount?: number;
  currentEndDate?: string;
  onSave: (amount: number, endDate: string) => void;
}

const BonusPointsDialog: React.FC<BonusPointsDialogProps> = ({
  open,
  onOpenChange,
  currentAmount = 0,
  currentEndDate = '',
  onSave
}) => {
  const [amount, setAmount] = useState<string>(currentAmount.toString());
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    if (open) {
      setAmount(currentAmount.toString());
      // Convert ISO date to YYYY-MM-DD format for input
      if (currentEndDate) {
        const date = new Date(currentEndDate);
        setEndDate(date.toISOString().split('T')[0]);
      } else {
        // Default to 30 days from now
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 30);
        setEndDate(defaultDate.toISOString().split('T')[0]);
      }
    }
  }, [open, currentAmount, currentEndDate]);

  const handleSave = () => {
    const pointsAmount = parseInt(amount) || 0;
    if (pointsAmount > 0 && endDate) {
      // Convert date to ISO string for database
      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999); // End of day
      onSave(pointsAmount, endDateTime.toISOString());
      onOpenChange(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for min attribute
  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            Bonus Points for New Customers
          </DialogTitle>
          <DialogDescription>
            Set how many bonus points new customers will receive and when this promotion ends.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="bonus-amount">Bonus Points Amount</Label>
            <Input
              id="bonus-amount"
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g., 25"
            />
            <p className="text-sm text-muted-foreground">
              Number of points to give each new customer
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="end-date">Promotion End Date</Label>
            <Input
              id="end-date"
              type="date"
              min={today}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              The bonus will automatically stop after this date
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!amount || parseInt(amount) <= 0 || !endDate}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BonusPointsDialog;
